import {
  Apartment as ApartmentIcon,
  Logout as LogoutIcon,
  SwitchAccount as SwitchAccountIcon,
} from '@mui/icons-material'
import {
  Avatar,
  alpha,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { T } from '@/helpers/T'
import { useSWRAxios } from '@/hooks/useSWRAxios'
import type { IEntitySimplified } from '@/modules/Entity/Entity'
import { useAuthContext } from '@/providers/Global'

//	///////////////////////
//	MARK: TYPES
export interface ICGlobalEntitySelectorProps extends Partial<React.ComponentProps<typeof Dialog>> {}

//	///////////////////////
//	MARK: HELPERS
const buildInitials = (label?: string): string => {
  if (!label) return ''
  return label
    .split(/\s+/)
    .map((word) => word[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

//	///////////////////////
//	MARK: EXPORTS
const GlobalEntitySelectorInternal: React.FC<ICGlobalEntitySelectorProps> = (props) => {
  const Auth = useAuthContext()

  const entitiesSWR = useSWRAxios<{ data: IEntitySimplified[] }>(
    '/api/entity?managedEntity=true&max=100',
    { revalidateOnFocus: false },
    true,
  )

  const entities = useMemo(() => entitiesSWR.data?.data.data ?? [], [entitiesSWR.data])
  const isLoading = !entitiesSWR.data && !entitiesSWR.error
  const hasNoGlobalEntity = !Auth.active!.globalEntity
  const shouldAutoSelect = !isLoading && hasNoGlobalEntity && entities.length === 1

  const [submittingId, setSubmittingId] = useState<number | undefined>(undefined)

  const submit = useCallback(
    (entity: IEntitySimplified) => {
      setSubmittingId(entity.id)
      Auth.updateGlobalEntity(Auth.active!.username, entity)
    },
    [Auth],
  )

  useEffect(() => {
    if (shouldAutoSelect) {
      submit(entities[0])
    }
  }, [shouldAutoSelect, entities, submit])

  const logout = useCallback(() => Auth.logout(), [Auth])

  const changeAccount = useCallback(() => Auth.changeSession(), [Auth])

  const onClose = useMemo<ICGlobalEntitySelectorProps['onClose']>(
    () =>
      (...args) => {
        Auth.changeSession()
        if (props.onClose) {
          props.onClose(...args)
        }
      },
    [Auth, props],
  )

  const open = (hasNoGlobalEntity || props.open) ?? false
  const showLoading = isLoading || shouldAutoSelect
  const showEmpty = !showLoading && entities.length === 0
  const showList = !showLoading && entities.length > 0

  return (
    <Dialog
      {...props}
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          elevation: 8,
          sx: {
            borderRadius: 2,
            width: 440,
            maxWidth: 'calc(100% - 32px)',
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          pt: 4,
          pb: 2,
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            mb: 2,
            width: 56,
            height: 56,
            borderRadius: '50%',
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ApartmentIcon sx={{ fontSize: 28 }} />
        </Box>
        <Typography component='div' variant='h6' sx={{ fontWeight: 700, lineHeight: 1.3 }}>
          <T id='lang.select_a_global_entity' />
        </Typography>
        <Typography
          component='div'
          variant='body2'
          color='text.secondary'
          sx={{ mt: 0.75, maxWidth: 360 }}
        >
          <T id='c.global_entity_selector.title' />
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {showLoading && (
          <Stack alignItems='center' spacing={1.5} sx={{ py: 5 }}>
            <CircularProgress size={32} />
            <Typography variant='body2' color='text.secondary'>
              <T id='c.global_entity_selector.loading' />
            </Typography>
          </Stack>
        )}

        {showEmpty && (
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ textAlign: 'center', py: 4, px: 2 }}
          >
            <T id='c.global_entity_selector.empty' />
          </Typography>
        )}

        {showList && (
          <Stack
            spacing={1}
            sx={{
              maxHeight: 320,
              overflowY: 'auto',
              pr: 0.5,
              mr: -0.5,
            }}
          >
            {entities.map((entity) => {
              const isSubmittingThis = submittingId === entity.id
              const isDisabled = submittingId !== undefined && !isSubmittingThis
              const initials = buildInitials(entity.eapcID ?? entity.fullName)
              return (
                <Box
                  key={entity.id}
                  onClick={() => !submittingId && submit(entity)}
                  onKeyDown={(event) => {
                    if ((event.key === 'Enter' || event.key === ' ') && !submittingId) {
                      event.preventDefault()
                      submit(entity)
                    }
                  }}
                  role='button'
                  tabIndex={isDisabled ? -1 : 0}
                  aria-disabled={isDisabled}
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    border: (theme) =>
                      `1px solid ${
                        isSubmittingThis ? theme.palette.primary.main : theme.palette.divider
                      }`,
                    bgcolor: (theme) =>
                      isSubmittingThis ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                    cursor: submittingId ? 'default' : 'pointer',
                    opacity: isDisabled ? 0.5 : 1,
                    pointerEvents: submittingId ? 'none' : 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    transition: 'background-color 150ms ease, border-color 150ms ease',
                    outline: 'none',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                    },
                    '&:focus-visible': {
                      borderColor: 'primary.main',
                      boxShadow: (theme) => `0 0 0 3px ${alpha(theme.palette.primary.main, 0.2)}`,
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      fontWeight: 700,
                      fontSize: '0.8rem',
                      bgcolor: isSubmittingThis ? 'primary.main' : 'action.selected',
                      color: isSubmittingThis ? 'primary.contrastText' : 'text.primary',
                    }}
                  >
                    {initials}
                  </Avatar>
                  <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Typography noWrap sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                      {entity.fullName}
                    </Typography>
                    {entity.eapcID && (
                      <Typography
                        variant='caption'
                        noWrap
                        sx={{
                          display: 'block',
                          color: 'text.secondary',
                          fontFamily: 'monospace',
                          letterSpacing: 0.2,
                        }}
                      >
                        {entity.eapcID}
                      </Typography>
                    )}
                  </Box>
                  {isSubmittingThis && <CircularProgress size={18} />}
                </Box>
              )
            })}
          </Stack>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          flexWrap: 'nowrap',
          gap: { xs: 0.5, sm: 1 },
          px: { xs: 1.5, sm: 2 },
          '& .MuiButton-root': {
            minWidth: 0,
            whiteSpace: 'nowrap',
            px: { xs: 1, sm: 2 },
          },
          '& .MuiButton-startIcon': {
            mr: { xs: 0.5, sm: 1 },
          },
        }}
      >
        <Button
          type='button'
          color='inherit'
          startIcon={<LogoutIcon />}
          onClick={logout}
          disabled={submittingId !== undefined}
          sx={{ mr: 'auto' }}
        >
          <T id='lang.logout' />
        </Button>
        <Button
          type='button'
          startIcon={<SwitchAccountIcon />}
          onClick={changeAccount}
          disabled={submittingId !== undefined}
        >
          <T id='lang.change_account' />
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export const GlobalEntitySelector = React.memo(
  GlobalEntitySelectorInternal,
) as typeof GlobalEntitySelectorInternal
